/**
 * Fait : les appels du client qui portent sur L'ARGENT — lire les budgets,
 *   poser le plafond global, poser celui d'un membre, et tenir le catalogue
 *   des modèles (ajouter une entrée, la modifier, l'activer ou l'éteindre).
 *   C'est le lot 7 du chantier `40.o` : les cinq dernières routes qui
 *   n'avaient aucune porte en ont une ici.
 *
 *   TOUT VOYAGE EN CENTIMES ENTIERS. Aucun montant à virgule ne traverse ce
 *   module : la traduction depuis ce qu'une personne écrit se fait dans
 *   `montants.ts`, avant d'arriver ici.
 *
 *   LA VERSION PART AVEC CHAQUE ÉCRITURE. Ni un budget ni une entrée de
 *   catalogue n'a de canal de diffusion — le direct ne parle que par projet.
 *   Deux onglets, ou deux parents, ne s'avertissent donc pas ; c'est la
 *   version qui rend le conflit lisible au lieu d'écraser en silence.
 *
 * Ne fait pas : il ne décide d'aucun droit — poser un plafond et tenir le
 *   catalogue n'appartiennent qu'au parent administrateur, et c'est la ROUTE
 *   qui refuse, jamais un bouton caché. Il n'affiche rien et ne convertit
 *   aucun montant.
 */

import type { Fournisseur, VoieDAuthentification } from '@aymoai/shared';

import { appelle } from './appel.ts';

/** Un budget tel que l'écran le reçoit — compteurs compris, rien de caché. */
export interface BudgetVu {
  readonly plafondCentimes: number;
  readonly engageCentimes: number;
  readonly consommeCentimes: number;
  readonly version: number;

  /**
   * Vrai si un plafond a déjà été posé. Faux ne veut pas dire « illimité » :
   * absent vaut ZÉRO, donc fermé. L'écran doit le dire, sans quoi une famille
   * croit avoir un budget ouvert alors que rien ne passera.
   */
  readonly pose: boolean;
}

/** La ligne d'un membre dans le tableau familial — réservée au parent. */
export interface LigneDeBudget extends BudgetVu {
  readonly compteId: string;
}

/** Ce que rend la lecture des budgets. Le tableau n'arrive qu'au parent. */
export interface BudgetsVus {
  readonly global: BudgetVu;
  readonly membre: BudgetVu;
  readonly tableau?: LigneDeBudget[];
}

/** Une entrée du catalogue, vue par qui l'administre — inactives comprises. */
export interface EntreeDeCatalogueVue {
  readonly id: string;
  readonly fournisseur: Fournisseur;
  readonly identifiantModele: string;
  readonly libelle: string;
  readonly voie: VoieDAuthentification;
  readonly actif: boolean;
  readonly releveLe: string;
  readonly version: number;
  readonly prixEntreeCentimesParMillionDeJetons?: number;
  readonly prixSortieCentimesParMillionDeJetons?: number;
}

/** Ce qu'on envoie pour inscrire un modèle neuf au catalogue. */
export type DemandeDeModele =
  | {
      readonly voie: 'cle_api';
      readonly fournisseur: Fournisseur;
      readonly identifiantModele: string;
      readonly libelle: string;
      readonly prixEntreeCentimesParMillionDeJetons: number;
      readonly prixSortieCentimesParMillionDeJetons: number;
    }
  | {
      readonly voie: 'abonnement';
      readonly fournisseur: Fournisseur;
      readonly identifiantModele: string;
      readonly libelle: string;
    };

/** Ce qu'on envoie pour retoucher une entrée — tout est facultatif sauf la version. */
export interface RetoucheDeModele {
  readonly version: number;
  readonly libelle?: string;
  readonly actif?: boolean;
  readonly prixEntreeCentimesParMillionDeJetons?: number;
  readonly prixSortieCentimesParMillionDeJetons?: number;
}

/**
 * Les budgets : le plafond global, le mien, et — pour le parent
 * administrateur seulement — le tableau de toute la famille.
 */
export function litLesBudgets() {
  return appelle<BudgetsVus>('/api/budget');
}

/** Pose le plafond GLOBAL, celui qui borne la famille entière. */
export function poseLePlafondGlobal(plafondCentimes: number, version: number) {
  return appelle<void>('/api/budget/global', {
    methode: 'PATCH',
    corps: { plafondCentimes, version },
  });
}

/** Pose le plafond d'UN membre. Sur un compte inconnu, la route rend 404. */
export function poseLePlafondDunMembre(compteId: string, plafondCentimes: number, version: number) {
  return appelle<void>(`/api/budget/membres/${compteId}`, {
    methode: 'PATCH',
    corps: { plafondCentimes, version },
  });
}

/**
 * Le catalogue COMPLET, inactives comprises — c'est ce que la route rend à qui
 * administre. L'écran du fil, lui, appelle la même route et n'y prend que les
 * modèles actifs : proposer un modèle éteint reviendrait à proposer une porte
 * fermée.
 */
export function litLeCatalogue() {
  return appelle<{ modeles: EntreeDeCatalogueVue[] }>('/api/catalogue');
}

/** Un modèle que le fournisseur propose, prix DÉJÀ convertis en euros (`D-036`). */
export interface ModeleProposeVu {
  readonly identifiantModele: string;
  readonly nom: string;
  readonly prixEntreeCentimesParMillionDeJetons: number;
  readonly prixSortieCentimesParMillionDeJetons: number;
}

/** Ce que la recherche rend, avec la DATE du cours qui a servi à convertir. */
export interface ModelesProposesVus {
  readonly modeles: ModeleProposeVu[];
  readonly coursDuLe: string;
  readonly combienEnTout: number;
}

/**
 * Cherche, chez le fournisseur, les modèles qui portent ce terme.
 *
 * La recherche voyage dans l'adresse et part à la SOUMISSION, pas à chaque
 * frappe : le serveur interroge un tiers à chaque appel, et une liste qui se
 * réordonne pendant qu'on tape est illisible pour qui la parcourt au clavier
 * ou à la voix — c'est déjà la règle de la bibliothèque de consignes.
 */
export function chercheDesModelesProposes(recherche: string) {
  const chemin = '/api/catalogue/proposes';

  return appelle<ModelesProposesVus>(`${chemin}?recherche=${encodeURIComponent(recherche.trim())}`);
}

/** Inscrit un modèle au catalogue. Le doublon est refusé par la base. */
export function inscritUnModele(demande: DemandeDeModele) {
  return appelle<{ id: string }>('/api/catalogue', { methode: 'POST', corps: demande });
}

/** Retouche une entrée — son nom, ses prix, ou son état actif. */
export function retoucheUnModele(entreeId: string, demande: RetoucheDeModele) {
  return appelle<void>(`/api/catalogue/${entreeId}`, { methode: 'PATCH', corps: demande });
}
